import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import { appPath, modelsPath } from "helpers/routePaths";
// Icon imports
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import { Link } from "@mui/material";
import {
	isoDateWithoutTimeZone,
	createTableHeaderClickHandler,
	defaultPageSize,
} from "helpers/utils";
import { useOutletContext } from "react-router-dom";
import { ReactComponent as ExternalLinkSvg } from "assets/icons/external-link.svg";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { getModelList } from "services/models/modelList";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useFilters } from "components/Elements/Filters";
// Init styled components
const AT = TableStyle();
// Size constant
const MAX_LOGO_HEIGHT = 47;

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableBody: {
		whiteSpace: "noWrap",
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: 130,
		[media]: {
			width: 130,
			// width: "auto",
		},
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 85,
	},
	nameLink: {
		color: ColourConstants.activeLink,
	},
	clientLogo: {
		maxHeight: MAX_LOGO_HEIGHT,
	},
	noImage: {
		color: ColourConstants.commonText,
		opacity: "50%",
	},
	lastCell: {
		borderBottom: "none",
	},
	greater: {
		color: ColourConstants.red,
	},
}));

const UserTable = forwardRef(({
	isSharableModel,
	data,
	setData,
	columns,
	headers,
	handleSort,
	handleDeleteDialogOpen,
	handleDuplicateModalOpen,
	handleViewVersionModalOpen,
	handleShareModelOpen,
	position,
	currentTableSort,
	reduceTableHeightBy,
	page,
	countOfModel,
	searchText,
	setDataForFetchingModel,
	chips,
}, ref) => {
	// Init hooks
	const { classes, cx } = useStyles();
	const [convertedData, setConvertedData] = useState([]);
	const { access } = useOutletContext();
	const { filterValues, buildUrlParams } = useFilters();
	const dispatch = useDispatch();

	// Get adminType from session storage
	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const adminType = me?.adminType;

	// Init State for scroll
	const [anchorElMenu, setAnchorElMenu] = useState(null);
	const [selectedDataMenu, setSelectedDataMenu] = useState(null);
	const [scrollEvent, setScrollEvent] = useState(window);
	const scrollRef = useRef(true);

	// Infinite scroll hook
	const { hasMore, loading, gotoTop, handleScroll } = useInfiniteScroll(
		data,
		countOfModel,
		async (pageSize, prevData, name) => {
			return await onPageChange(pageSize + 1, prevData, name);
		},
		page,
		searchText,
		scrollEvent,
		{
			...buildUrlParams(),
			sortField: currentTableSort[0],
			sortOrder: currentTableSort[1],
			search: searchText,
			page,
			chips: chips || [],
		}
	);


	// Expose gotoTop function to parent component
	useImperativeHandle(ref, () => ({
		gotoTop
	}));

	// Update scroll container reference and attach event listener for infinite scroll
	useEffect(() => {
		if (data) {
			if (scrollRef.current === true) {
				const scrollContainer = document.getElementById("table-scroll-wrapper-container");

				if (scrollContainer) {
					// Attach scroll event listener
					scrollContainer.addEventListener("scroll", (e) =>
						handleScroll(e, "table-scroll-wrapper-container")
					);
					setScrollEvent(scrollContainer);
					scrollRef.current = false;
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	// Pagination handler
	const onPageChange = async (p, prevData, name) => {
		try {
			const currentFilters = buildUrlParams();
			const response = await getModelList({
				id: position?.siteAppID || 24,
				pageNumber: p,
				pageSize: defaultPageSize(),
				search: searchText,
				...currentFilters,
				sortField: currentTableSort[0],
				sortOrder: currentTableSort[1],
				chips: chips || [],
				...name,
			});
			if (response.status) {
				// Ensure response.data is an array
				const responseData = Array.isArray(response.data) ? response.data : [];

				setDataForFetchingModel((prev) => ({ ...prev, pageNumber: p }));

				// Map the data with fullName
				const mappedData = responseData.map((d) => ({
					...d,
					serialNumberRange: d?.serialNumberRange ?? "",
					fullName:
						d?.name +
						" " +
						(d?.modelName === null || d?.modelName === undefined
							? ""
							: d?.modelName),
				}));

				// Ensure prevData is treated as an array
				const newData = Array.isArray(prevData) ? [...prevData, ...mappedData] : [...mappedData];
				setData(newData);
				response.data = newData;

				return response;
			} else {
				throw new Error(response);
			}
		} catch (err) {
			dispatch(showError("Failed to load models"));
			return err;
		}
	};

	useEffect(() => {
		// Clear convertedData first to force React to unmount old components
		setConvertedData([]);

		// Ensure data is an array before mapping
		const safeData = Array.isArray(data) ? data : [];
		let newData = safeData.map((item, index) => {
			let currentDate = new Date();
			let apiDate = isoDateWithoutTimeZone(
				item?.reviewDate ? item.reviewDate + "Z" : item.reviewDate
			);
			let isGreater =
				currentDate > new Date(item?.reviewDate ? item?.reviewDate + "Z" : "");
			return {
				...item,
				status: `${item?.status} ${
					item?.activeModelVersion
						? item?.active
							? " (Active)"
							: " (Inactive)"
						: ""
				}`,
				reviewDate: (
					<span className={`${isGreater ? classes.greater : ""}`}>
						{apiDate?.split(" ")[0]}
					</span>
				),
				departments: item?.departments?.join(", "), // join arrays with comma
				createdDate: item?.createdDate
					? isoDateWithoutTimeZone(item.createdDate + "Z")
					: "",
				modifiedDate: item?.modifiedDate
					? isoDateWithoutTimeZone(item.modifiedDate + "Z")
					: "",
			};
		});

		setConvertedData(newData);
	}, [data]);

	// Handlers
	const handleSortClick = createTableHeaderClickHandler(
		currentTableSort,
		handleSort
	);

	// columns for tooltip
	const toolTipColumn = ["departments"];

	const handleTooltip = (col) => {
		//the query selector is not getting the repective ID . Returns null.
		const tooltipElement = document.querySelector(`#tooltip-${col} > div`);

		if (tooltipElement) {
			if (col === "departments") {
				const departmentsData = document.querySelector(
					`#taskExpandable${col.id} > #dataCellroles > div > p `
				);
				if (departmentsData?.textContent) {
					document.querySelector(`#tooltip-roles > div `).innerHTML =
						departmentsData;
				}
			}
		}
	};
	return (
		<>
			<AutoFitContentInScreen
				containsTable={true}
				reduceTableHeightBy={reduceTableHeightBy}
			>
				<Table aria-label="Table" className={classes.table}>
					<AT.TableHead>
						<TableRow>
							{Array.isArray(headers) && headers.map((header, i) => (
								<TableCell
									key={i}
									onClick={() => {
										handleSortClick(columns[i]);
									}}
									className={cx(classes.nameRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[i],
										[classes.tableHeadRow]: currentTableSort[0] !== columns[i],
									})}
								>
									<AT.CellContainer>
										{header}
										{currentTableSort[0] === columns[i] &&
										currentTableSort[1] === "desc" ? (
											<AT.DefaultArrow fill="#FFFFFF" />
										) : (
											<AT.DescArrow fill="#FFFFFF" />
										)}
									</AT.CellContainer>
								</TableCell>
							))}
						</TableRow>
					</AT.TableHead>
					<TableBody className={classes.tableBody}>
						{Array.isArray(convertedData) && convertedData.length ? (
							convertedData.map((row, index) => (
								<TableRow key={`${row.id}-${index}`}>
									{Array.isArray(columns) && columns.map((col, i, arr) => {
										return (
											<AT.DataCell
												key={col}
												style={
													toolTipColumn.includes(col)
														? {
																padding: "0 10px",
																width: "200px",
																height: "44px",
														  }
														: {}
												}
											>
												<AT.CellContainer key={col}>
													<AT.TableBodyText>
														{col === "fullName" ? (
															<Link
																underline="none"
																onClick={(e) => {
																	// prevents left-click from opening the link in the same tab
																	if (e?.button !== 2) {
																		e.preventDefault();
																		window.open(
																			`${appPath}${modelsPath}/${row.devModelVersionID}`,
																			"_blank"
																		);
																	}
																}}
																href={`${appPath}${modelsPath}/${row.devModelVersionID}`}
																style={{
																	color: "rgb(17, 100, 206)",
																	cursor: "pointer",
																	display: "flex",
																	justifyContent: "flex-start",
																	alignItems: "center",
																	gap: 10,
																}}
															>
																{row[col]}
																<ExternalLinkSvg
																	style={{
																		width: "16px",
																		height: "16px",
																		marginBottom: 3,
																	}}
																/>
															</Link>
														) : toolTipColumn.includes(col) ? (
															<HtmlTooltip
																id={`tooltip-${col}`}
																title={row[col]}
																onOpen={() => handleTooltip(col)}
															>
																<p
																	className="max-two-line"
																	draggable="true"
																	style={{
																		padding: "0 10px",
																		width: "200px",
																		height: "44px",
																		textOverflow: "ellipsis",
																		whiteSpace: "wrap",
																	}}
																>
																	{row[col]}
																</p>
															</HtmlTooltip>
														) : (
															row[col]
														)}
													</AT.TableBodyText>

													{arr.length === i + 1 ? (
														<AT.DotMenu
															onClick={(e) => {
																setAnchorElMenu(
																	anchorElMenu === e.currentTarget
																		? null
																		: e.currentTarget
																);
																setSelectedDataMenu(
																	anchorElMenu === e.currentTarget ? null : index
																);
															}}
														>
															<AT.TableMenuButton>
																<MenuIcon />
															</AT.TableMenuButton>

															<PopupMenu
																index={index}
																selectedData={selectedDataMenu}
																anchorEl={anchorElMenu}
																isLast={index === data.length - 1}
																id={row.id}
																clickAwayHandler={() => {
																	setAnchorElMenu(null);
																	setSelectedDataMenu(null);
																}}
																menuData={[
																	{
																		name:
																			access === "R" ||
																			row.activeModelVersionID ===
																				row.devModelVersionID
																				? "View"
																				: "Edit",
																		handler: () => {
																			window.open(
																				`${appPath}${modelsPath}/${row.devModelVersionID}`
																			);
																		},
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "View Version",
																		handler: handleViewVersionModalOpen,
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "Duplicate",
																		handler: () => {
																			handleDuplicateModalOpen(row);
																		},
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "Share",
																		handler: () => {
																			handleShareModelOpen(row);
																		},
																		isDelete: false,
																		ShouldHide: !(
																			isSharableModel &&
																			(row.isSharable || adminType === 1) &&
																			row.isPublish
																		),
																	},
																	{
																		name: "Remove",
																		handler: handleDeleteDialogOpen,
																		isDelete: true,
																		ShouldHide: !position?.allowPublish,
																	},
																].filter((x) => {
																	if (access === "F" && !x.ShouldHide)
																		return true;
																	if (access === "E" && !x.ShouldHide) {
																		if (
																			x.name === "Edit" ||
																			x.name === "View" ||
																			x.name === "View Version"
																		) {
																			return true;
																		} else {
																			return false;
																		}
																	}
																	if (access === "R" && !x.ShouldHide) {
																		if (
																			x.name === "Edit" ||
																			x.name === "View" ||
																			x.name === "View Version"
																		)
																			return true;
																		else return false;
																	}
																	return false;
																})}
															/>
														</AT.DotMenu>
													) : null}
												</AT.CellContainer>
											</AT.DataCell>
										);
									})}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell>No any records found</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</AutoFitContentInScreen>
		</>
	);
});

export default UserTable;
